import { ClientTag, UserRoleName } from './types/UserProfile';

export type ClientDivisionOption = { id: string; value: number; label: string };

/**
 * Options for the division picker, scoped to whichever client tags are
 * currently selected on the form. A division always belongs to exactly one
 * client tag and a client's own divisions already come back nested on it —
 * there's no separate division-search endpoint.
 *
 * Keeps any division the user is currently assigned to, even if it has since
 * been deactivated or its client tag isn't in the catalog, so an existing
 * assignment never silently disappears from the picker. A division whose
 * client tag *is* in the catalog but is no longer selected is dropped though:
 * deselecting a client tag has to take that client's divisions with it.
 */
export function getClientDivisionOptions(
  clientTags: ClientTag[],
  selectedClientTagIds: Array<string | number>,
  currentlyAssignedDivisions: ClientDivisionOption[] = [],
): ClientDivisionOption[] {
  const selectedIds = new Set(selectedClientTagIds.map(String));
  const options = new Map<string, ClientDivisionOption>();
  const divisionsOfDeselectedTags = new Set<string>();

  clientTags.forEach((tag) => {
    if (!selectedIds.has(String(tag.id))) {
      (tag.divisions || []).forEach((division) => divisionsOfDeselectedTags.add(String(division.id)));
      return;
    }

    (tag.divisions || [])
      .filter((division) => division.is_active !== false)
      .forEach((division) => {
        options.set(String(division.id), { id: String(division.id), value: division.id, label: division.name });
      });
  });

  currentlyAssignedDivisions.forEach((division) => {
    if (divisionsOfDeselectedTags.has(String(division.id)) || options.has(String(division.id))) {
      return;
    }
    options.set(String(division.id), division);
  });

  return Array.from(options.values());
}

/**
 * Ids of the divisions that belong to `selectedClientTagIds` — use it to prune a form's
 * division selection as soon as the client tags change, so a division whose client tag was
 * just removed goes with it instead of lingering in a hidden field and being submitted.
 */
export function getAllowedDivisionIds(
  clientTags: ClientTag[],
  selectedClientTagIds: Array<string | number>,
  currentlyAssignedDivisions: ClientDivisionOption[] = [],
): Set<string> {
  return new Set(
    getClientDivisionOptions(clientTags, selectedClientTagIds, currentlyAssignedDivisions).map((option) =>
      String(option.id),
    ),
  );
}

export type ClientDivisionGroup = {
  clientTagId: string;
  clientTagName: string;
  options: ClientDivisionOption[];
  /** Set by [scopeDivisionGroupsToClientAdmin] on a tag the Client Admin is confined in. */
  required?: boolean;
};

/**
 * Grouped per client tag so forms can render a picker each, the way organizations do.
 *
 * A tag absent from the catalog is one this actor does not administer, so it gets no group
 * and its divisions stay off the page entirely. They remain in `getClientDivisionOptions`,
 * so `getAllowedDivisionIds` still treats them as allowed and no picker rewrites them: the
 * assignment survives the save untouched. That is the point, because the server scopes
 * divisions being assigned but not ones being dropped.
 */
export function getClientDivisionOptionGroups(
  clientTags: ClientTag[],
  selectedClientTagIds: Array<string | number>,
  currentlyAssignedDivisions: ClientDivisionOption[] = [],
): ClientDivisionGroup[] {
  const optionsById = new Map(
    getClientDivisionOptions(clientTags, selectedClientTagIds, currentlyAssignedDivisions).map((option) => [
      String(option.id),
      option,
    ]),
  );
  const selectedIds = new Set(selectedClientTagIds.map(String));
  const groups: ClientDivisionGroup[] = [];

  clientTags.forEach((tag) => {
    if (!selectedIds.has(String(tag.id))) return;

    const options = (tag.divisions || [])
      .map((division) => optionsById.get(String(division.id)))
      .filter((option): option is ClientDivisionOption => Boolean(option));

    if (options.length > 0) {
      groups.push({ clientTagId: String(tag.id), clientTagName: tag.name, options });
    }
  });

  return groups;
}

/** Assigned divisions bucketed by owning client tag, for read-only views; same scoping rule as above. */
export function getAssignedClientDivisionGroups(
  clientTags: ClientTag[],
  assignedDivisions: Array<{ id: number | string; name: string }>,
): ClientDivisionGroup[] {
  const assignedById = new Map<string, ClientDivisionOption>(
    assignedDivisions.map((division) => [
      String(division.id),
      { id: String(division.id), value: Number(division.id), label: division.name },
    ]),
  );
  // Self-views never fetch a catalog, so there is nothing to group by and nothing to scope
  // against either: fall back to one unnamed group rather than showing the user no divisions.
  if (clientTags.length === 0) {
    return assignedById.size > 0
      ? [{ clientTagId: '', clientTagName: '', options: Array.from(assignedById.values()) }]
      : [];
  }

  const groups: ClientDivisionGroup[] = [];

  clientTags.forEach((tag) => {
    const options = (tag.divisions || [])
      .map((division) => assignedById.get(String(division.id)))
      .filter((option): option is ClientDivisionOption => Boolean(option));

    if (options.length > 0) {
      groups.push({ clientTagId: String(tag.id), clientTagName: tag.name, options });
    }
  });

  return groups;
}

/**
 * Narrows division groups to what a Client Admin may manage, per client the way the server
 * scopes it: a tag they hold whole keeps all of its divisions, a tag they are confined in keeps
 * only the divisions they belong to and is marked `required`, since a user left with none of
 * those divisions drops out of the admin's view. Any other actor gets the groups back unchanged.
 * A null actor means their divisions are still loading, so no group is offered yet.
 *
 * Divisions filtered out here stay in the form value untouched, since each picker only rewrites
 * its own options' ids, so the save carries them forward instead of dropping them.
 */
export function scopeDivisionGroupsToClientAdmin(
  groups: ClientDivisionGroup[],
  clientTags: ClientTag[],
  actor: { role?: { name?: string } | null; divisions?: Array<{ id: number | string }> | null } | null | undefined,
): ClientDivisionGroup[] {
  if (!actor) return [];
  if (actor.role?.name !== UserRoleName.OrganizationAdmin) return groups;

  const actorDivisionIds = new Set((actor.divisions || []).map((division) => String(division.id)));
  if (actorDivisionIds.size === 0) return groups;

  return groups
    .map((group) => {
      const tag = clientTags.find((candidate) => String(candidate.id) === group.clientTagId);
      const confinedTo = (tag?.divisions || [])
        .map((division) => String(division.id))
        .filter((id) => actorDivisionIds.has(id));
      if (confinedTo.length === 0) return group;
      return {
        ...group,
        options: group.options.filter((option) => confinedTo.includes(String(option.id))),
        required: true,
      };
    })
    .filter((group) => group.options.length > 0);
}

/** True when a `required` division group has none of its options among `selectedDivisionIds`. */
export function hasUnfilledRequiredDivisionGroup(
  groups: ClientDivisionGroup[],
  selectedDivisionIds: Array<string | number>,
): boolean {
  const selected = new Set(selectedDivisionIds.map(String));
  return groups.some((group) => group.required && !group.options.some((option) => selected.has(String(option.id))));
}
